import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { getApp } from '@angular/fire/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ConfirmationService } from 'primeng/api';

import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FirestoreService } from '../services/firestore';
import { DataService } from '../services/data';
import { Funciones } from '../services/funciones';
import { ToastService } from '../services/toast.service';
import { MissionMoneyPipe } from '../pipes/mission-money.pipe';
import { env } from '../env.generated';
import {
  HISTORIAL_UI_INITIAL,
  historialHasMore,
  nextHistorialVisibleCount,
} from '../utils/historial.util';

interface HistorialCambioView {
  fecha: string;
  byName: string;
  action: 'create' | 'update' | 'restore';
  resumen?: string;
  campos?: string[];
}

@Component({
  selector: 'app-eliminados',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    DialogModule,
    TooltipModule,
    TagModule,
    TranslateModule,
    MissionMoneyPipe,
  ],
  templateUrl: './eliminados.html',
  styleUrls: ['./eliminados.css'],
})
export class Eliminados implements OnInit, OnDestroy {
  private firestoreService = inject(FirestoreService);
  private dataService = inject(DataService);
  private funciones = inject(Funciones);
  private confirmationService = inject(ConfirmationService);
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);

  private subscriptions = new Subscription();
  private churchId: string | null = null;
  private lastDoc: any = null;

  /** Primera carga trae como máximo 100 documentos. */
  public readonly initialPageSize = 100;
  /** Cada "mostrar más" trae 50 adicionales. */
  public readonly morePageSize = 50;

  public rows: any[] = [];
  /** Indica si el usuario ya solicitó la carga (o si se hidrató desde cache). */
  public loaded = false;
  public loading = false;
  public hasMore = false;

  public detailVisible = false;
  public selected: any | null = null;
  public selectedHistorial: HistorialCambioView[] = [];
  public historialVisibleCount = HISTORIAL_UI_INITIAL;
  public canRestore = false;
  public restoringId: string | null = null;

  ngOnInit(): void {
    this.subscriptions.add(
      this.dataService.churchId$.subscribe(churchId => {
        if (churchId !== this.churchId) {
          this.churchId = churchId;
          this.hydrateFromCache();
        }
      })
    );
    this.subscriptions.add(
      this.dataService.userRoles$.subscribe(() => this.refreshCanRestore())
    );
    this.subscriptions.add(
      this.dataService.supportAccess$.subscribe(() => this.refreshCanRestore())
    );
    this.refreshCanRestore();
  }

  private refreshCanRestore(): void {
    const roles = this.dataService.getUserRoles() || [];
    const isLeader = roles.some((role) => ['Admin', 'Pastor'].includes(role));
    this.canRestore = this.dataService.getSupportAccess() || isLeader;
  }

  canRestoreRow(row: any): boolean {
    const isMovimiento =
      row?.entityType === 'movimiento' ||
      (!row?.entityType && !!row?.snapshot?.tipoMovimiento);
    return this.canRestore && isMovimiento && row?.restaurado !== true && !!row?.id;
  }

  isRestored(row: any): boolean {
    return row?.restaurado === true;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Al entrar (o volver) al componente, intenta reutilizar lo ya cargado en DataService.
   * No consulta Firestore: si no hay cache, queda a la espera de que el usuario pulse "Cargar".
   */
  private hydrateFromCache(): void {
    this.rows = [];
    this.lastDoc = null;
    this.hasMore = false;
    this.loaded = false;

    if (!this.churchId) return;

    const cache = this.dataService.getEliminadosCache(this.churchId);
    if (cache) {
      this.rows = cache.items;
      this.lastDoc = cache.lastDoc;
      this.hasMore = cache.hasMore;
      this.loaded = true;
    }
  }

  /** Primera solicitud explícita del Pastor/Admin: trae hasta 100 documentos. */
  async cargar(): Promise<void> {
    if (!this.churchId || this.loading) return;
    this.loading = true;
    try {
      const { items, lastDoc } = await this.firestoreService.getEliminadosPage(
        this.churchId,
        this.initialPageSize,
        null
      );
      this.rows = items;
      this.lastDoc = lastDoc;
      this.hasMore = items.length === this.initialPageSize;
      this.loaded = true;
      this.persistCache();
    } finally {
      this.loading = false;
    }
  }

  /** Trae 50 documentos adicionales y los agrega a la lista. */
  async cargarMas(): Promise<void> {
    if (!this.churchId || this.loading || !this.hasMore) return;
    this.loading = true;
    try {
      const { items, lastDoc } = await this.firestoreService.getEliminadosPage(
        this.churchId,
        this.morePageSize,
        this.lastDoc
      );
      this.rows = [...this.rows, ...items];
      this.lastDoc = lastDoc;
      this.hasMore = items.length === this.morePageSize;
      this.persistCache();
    } finally {
      this.loading = false;
    }
  }

  /** Fuerza recarga desde Firestore (descarta cache). */
  recargar(): void {
    if (!this.churchId) return;
    this.dataService.clearEliminadosCache();
    this.rows = [];
    this.lastDoc = null;
    this.hasMore = false;
    this.loaded = false;
    this.cargar();
  }

  private persistCache(): void {
    if (!this.churchId) return;
    this.dataService.setEliminadosCache(this.churchId, this.rows, this.lastDoc, this.hasMore);
  }

  formatFecha(value: any): string {
    if (!value) return '—';
    const jsDate = value?.toDate ? value.toDate() : new Date(value);
    return this.funciones.convertirFechaHoraMinutos(jsDate, false);
  }

  getTipo(row: any): string {
    return row?.snapshot?.tipoMovimiento || '—';
  }

  getTipoSeverity(row: any): 'success' | 'danger' | 'info' {
    const tipo = row?.snapshot?.tipoMovimiento;
    if (tipo === 'Ingreso') return 'success';
    if (tipo === 'Egreso') return 'danger';
    return 'info';
  }

  getConcepto(row: any): string {
    const s = row?.snapshot || {};
    return s.concepto || s.descripcion || s.codigo || '—';
  }

  getMonto(row: any): number {
    return Number(row?.snapshot?.monto ?? 0);
  }

  openDetail(row: any): void {
    this.selected = row;
    const entries: any[] = Array.isArray(row?.snapshot?.historialCambios)
      ? row.snapshot.historialCambios
      : [];
    this.selectedHistorial = entries
      .map(entry => ({
        fecha: this.formatFecha(entry?.at),
        byName: entry?.byName || '—',
        action:
          entry?.action === 'create'
            ? 'create'
            : entry?.action === 'restore'
              ? 'restore'
              : 'update',
        resumen: entry?.resumen || undefined,
        campos: Array.isArray(entry?.campos) ? entry.campos : undefined,
      } as HistorialCambioView))
      .reverse();
    this.historialVisibleCount = HISTORIAL_UI_INITIAL;
    this.detailVisible = true;
  }

  get visibleSelectedHistorial(): HistorialCambioView[] {
    return this.selectedHistorial.slice(0, this.historialVisibleCount);
  }

  get hasMoreSelectedHistorial(): boolean {
    return historialHasMore(this.historialVisibleCount, this.selectedHistorial.length);
  }

  loadMoreHistorial(): void {
    this.historialVisibleCount = nextHistorialVisibleCount(
      this.historialVisibleCount,
      this.selectedHistorial.length
    );
  }

  closeDetail(): void {
    this.detailVisible = false;
    this.selected = null;
    this.selectedHistorial = [];
    this.historialVisibleCount = HISTORIAL_UI_INITIAL;
  }

  historyActionKey(action: HistorialCambioView['action']): string {
    if (action === 'create') return 'FINANCE.HISTORY_ACTION_CREATE';
    if (action === 'restore') return 'DELETED.HISTORY_ACTION_RESTORE';
    return 'FINANCE.HISTORY_ACTION_UPDATE';
  }

  requestRestore(row: any): void {
    if (!this.canRestoreRow(row) || this.restoringId) return;
    const concepto = this.getConcepto(row);
    const monto = this.getMonto(row);
    this.confirmationService.confirm({
      header: this.translate.instant('DELETED.RESTORE_CONFIRM_TITLE'),
      message: this.translate.instant('DELETED.RESTORE_CONFIRM_MSG', {
        concepto,
        amount: monto,
      }),
      icon: 'pi pi-replay',
      acceptLabel: this.translate.instant('DELETED.RESTORE'),
      rejectLabel: this.translate.instant('COMMON.CANCEL'),
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        void this.restoreRow(row);
      },
    });
  }

  private async restoreRow(row: any): Promise<void> {
    if (!this.churchId || !row?.id || this.restoringId) return;
    this.restoringId = row.id;
    try {
      const region = env.NG_APP_FUNCTIONS_REGION || undefined;
      const functions = region ? getFunctions(getApp(), region) : getFunctions(getApp());
      const fn = httpsCallable(functions, 'restaurarMovimiento');
      await fn({ churchId: this.churchId, tombstoneId: row.id });
      this.toastService.showSuccess(this.translate.instant('DELETED.RESTORE_OK'));
      if (this.selected?.id === row.id) this.closeDetail();
      this.recargar();
    } catch (e: any) {
      this.toastService.showError(
        e?.message || this.translate.instant('DELETED.RESTORE_ERR')
      );
    } finally {
      this.restoringId = null;
    }
  }

  getSnapshotEntries(): { key: string; value: any }[] {
    const snapshot = this.selected?.snapshot;
    if (!snapshot) return [];
    const hidden = new Set(['historialCambios']);
    return Object.keys(snapshot)
      .filter(k => !hidden.has(k))
      .sort()
      .map(key => ({ key, value: this.stringifyValue(snapshot[key]) }));
  }

  private stringifyValue(value: any): string {
    if (value == null) return '—';
    if (value?.toDate) return this.formatFecha(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }
}
